import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { Alert, Box, Button, Heading, Spinner, useToast } from '@razorpay/blade/components';
import { CustomFieldsForm } from './CustomFieldsForm';
import { getEditableFields } from './utils';
import { useEmployeeCustomFields } from './hooks/useEmployeeCustomFields';
import { updateEmployeeCustomFields } from 'api/gql-utils/employeeCustomFields/mutations/updateEmployeeCustomFields';
import { validateEmployeeCustomFields } from 'api/gql-utils/employeeCustomFields/mutations/validateEmployeeCustomFields';
import { PayrollFieldViolation } from 'api/gql-utils/employeeCustomFields/types';
import { generateUserId } from 'utils/commons';
import { CustomFieldValueMap } from './types';
import { encodeFieldValuesForSubmit, validateCustomFieldValues } from './validation';
import { getClientSafeMessage, isForbiddenError } from './errors';

export const EmployeeOtherInfoEdit = () => {
  // Route param is the bare numeric id (the legacy PHP detail page links with it);
  // the HRIS GraphQL BFF requires the `user_`-prefixed id on all operations.
  const { userId } = useParams<{ userId: string }>();
  const prefixedUserId = generateUserId(userId);
  const toast = useToast();
  const { definitions, values, isLoading, isError, error, refetch } = useEmployeeCustomFields(prefixedUserId);
  const [draft, setDraft] = useState<CustomFieldValueMap>({});
  const [violations, setViolations] = useState<PayrollFieldViolation[]>([]);

  const merged: CustomFieldValueMap = { ...values, ...draft };
  const editableKeys = getEditableFields(definitions).map((d) => d.fieldKey);

  const buildPayload = (): CustomFieldValueMap =>
    Object.fromEntries(Object.entries(merged).filter(([key]) => editableKeys.includes(key)));

  const saveMutation = useMutation(
    async () => {
      setViolations([]);
      const payload = buildPayload();
      // Client-side required + type-format checks first, for instant field-level
      // feedback; the server-side validate call below remains the authoritative
      // backstop (regex/cross-field rules, enum membership).
      const clientViolations = validateCustomFieldValues(definitions, payload);
      if (clientViolations.length > 0) {
        setViolations(clientViolations);
        throw new Error('validation');
      }
      // HRIS expects MONEY values as { amount_paise } objects — encode at the boundary.
      const encodedPayload = encodeFieldValuesForSubmit(definitions, payload);
      const validation = await validateEmployeeCustomFields({
        userId: prefixedUserId,
        fields: encodedPayload,
      });
      const errors = (validation.payrollValidateEmployeeCustomFields.violations ?? []).filter(
        (v) => v.severity === 'ERROR',
      );
      if (errors.length > 0) {
        setViolations(validation.payrollValidateEmployeeCustomFields.violations ?? []);
        throw new Error('validation');
      }
      return updateEmployeeCustomFields({
        userId: prefixedUserId,
        fields: encodedPayload,
        mode: 'MERGE',
      });
    },
    {
      onSuccess: () => {
        toast.show({ content: 'Other information updated', color: 'positive' });
        window.location.href = `/employeeDetail?userId=${userId}`;
      },
      onError: (e: Error) => {
        if (e.message !== 'validation') {
          // Surface the server's client-safe message (FIELD_VALIDATION_FAILED /
          // CONFLICT / FORBIDDEN / NOT_FOUND) when present; generic fallback otherwise.
          const serverMessage = getClientSafeMessage(e);
          toast.show({
            content: serverMessage || 'Failed to update other information. Please retry.',
            color: 'negative',
          });
        }
      },
    },
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding="spacing.10">
        <Spinner accessibilityLabel="Loading" />
      </Box>
    );
  }

  if (isError) {
    // FORBIDDEN (e.g. editing another employee's fields without EDIT_EMPLOYEES)
    // is an access decision, not an outage — no Retry button.
    if (isForbiddenError(error)) {
      return (
        <Box padding="spacing.7">
          <Alert
            color="notice"
            title="Access restricted"
            description="You don't have access to view these details."
          />
        </Box>
      );
    }
    return (
      <Box padding="spacing.7">
        <Alert
          color="negative"
          title="Could not load fields"
          description="Please retry."
          actions={{ primary: { text: 'Retry', onClick: refetch } }}
        />
      </Box>
    );
  }

  return (
    // Legacy pages constrain content to a 1275px column (.app__main) whose right
    // edge reads as a divider; mirror it so this page aligns with the PHP edit
    // pages the user navigates between.
    <Box
      maxWidth="1275px"
      minHeight="100vh"
      borderRightColor="surface.border.gray.subtle"
      borderRightWidth="thin">
      <Box padding="spacing.7" maxWidth="720px" margin="auto">
      <Heading size="large" marginBottom="spacing.6">
        Edit Other Information
      </Heading>
      <CustomFieldsForm
        definitions={definitions}
        values={merged}
        onChange={(fieldKey, value) => setDraft((d) => ({ ...d, [fieldKey]: value }))}
        violations={violations}
        showViewFieldsReadOnly
      />
      <Box display="flex" gap="spacing.4" marginTop="spacing.7">
        <Button isLoading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
          Save
        </Button>
        <Button
          variant="tertiary"
          isDisabled={saveMutation.isLoading}
          onClick={() => (window.location.href = `/employeeDetail?userId=${userId}`)}>
          Cancel
        </Button>
      </Box>
      </Box>
    </Box>
  );
};

export default EmployeeOtherInfoEdit;
